// Label priority for FieldPilot items: a grouped item shows its sub-task (sub-category)
// title; an ungrouped item shows its parent tag name. Shared by the viewport overlay and
// the progress table so labels stay consistent everywhere an item is rendered.

/** Minimal tag shape for picker/display labels (schedule override or raw value). */
export interface TagLabelSource {
  rawValue: string;
  displayName?: string | null;
}

/**
 * Tag label shown in FieldPilot pickers and lists: the schedule task-name override when set,
 * otherwise the raw tag value. Matches backend `tagLabel`.
 */
export function resolveTagLabel(tag: TagLabelSource): string {
  return tag.displayName?.trim() || tag.rawValue;
}

/**
 * Shared value for a multi-select picker: the common id when every selected item
 * matches, otherwise an empty value so choosing any option (including the first
 * item's current id) still fires onValueChange.
 */
export function sharedSelectValue(ids: Iterable<string>): {
  value: string;
  isMixed: boolean;
} {
  const uniqueIds = [...new Set(ids)];
  if (uniqueIds.length === 1) {
    return { value: uniqueIds[0] ?? "", isMixed: false };
  }
  return { value: "", isMixed: uniqueIds.length > 1 };
}

export interface ResolveItemLabelArgs {
  /** The item's sub-task id, when it has been grouped into one. */
  subTaskId?: string | null;
  /** Map of sub-task id → sub-task name (e.g. derived from `subTasks.listByJob`). */
  subTaskNameById: Map<string, string>;
  /** The parent tag's display label, used when the item is ungrouped. */
  tagName: string;
}

/**
 * Resolve the label to show for a FieldPilot item.
 *
 * Returns the sub-task name when the item is grouped and that sub-task is known; otherwise
 * falls back to the parent tag name. The tag name is also used when a sub-task id is present
 * but missing from the lookup (e.g. a freshly archived sub-task) so a label always renders.
 */
export function resolveItemLabel({
  subTaskId,
  subTaskNameById,
  tagName,
}: ResolveItemLabelArgs): string {
  if (subTaskId) {
    const subTaskName = subTaskNameById.get(String(subTaskId));
    if (subTaskName && subTaskName.trim()) {
      return subTaskName;
    }
  }
  return tagName;
}

/** The plan-markup label's optional annotation: off / next / current / all milestones. */
export type FieldPilotAnnotationMode = "off" | "next" | "current" | "all";

/** Minimal status shape used to resolve annotations (sorted by pipeline displayOrder). */
export interface AnnotationStatus {
  _id: string;
  name: string;
  isCompletionStatus?: boolean;
}

/** Minimal sub-task status-line entry used to resolve annotations. */
export interface AnnotationStatusLineEntry {
  statusId: string;
  name: string | null;
  rank: number;
  dueDateKey: string | null;
}

/**
 * Resolve the annotation line(s) shown under a plan-markup item's name. Multi-line modes join
 * with `\n` so the viewport/PDF can render each milestone on its own line. Upcoming "next"
 * lines never show a status without a due date, following this strict precedence:
 *
 * - "off": no line.
 * - "current": the item's current status name.
 * - "all": every dated milestone on the status line, each as "Status by date", joined by `\n`.
 * - "next":
 *   1. The next dated milestone the item must still reach — the lowest-rank `statusLine` entry with
 *      a `dueDateKey` whose `rank > itemRank` ("→ Installed by 7/4").
 *   2. Else, when the item is not final and the parent task has a top-level finish date, the final
 *      status due by that date ("→ Inspected by 7/10").
 *   3. Else, when the item is final, the current/completion status and the date associated with it
 *      ("Inspected by 7/10") — own milestone due date, else the task finish when this is the last
 *      pipeline status. Status name alone if no date is available. Unnamed final statuses fall
 *      back to "Finished".
 *
 * Where `isFinal` is true when the current status is a completion status or the last status in the
 * pipeline, and `finalStatusName` is the name of the last status in `statuses`.
 */
export function resolveItemAnnotation({
  mode,
  statusId,
  statuses,
  statusLine,
  taskFinishDateKey,
  formatDate,
}: {
  mode: FieldPilotAnnotationMode;
  statusId: string | null | undefined;
  statuses: readonly AnnotationStatus[];
  statusLine?: readonly AnnotationStatusLineEntry[] | null;
  taskFinishDateKey: string | null | undefined;
  formatDate: (dateKey: string) => string;
}): string | null {
  if (mode === "off") return null;

  const statusIndex = statuses.findIndex((status) => String(status._id) === String(statusId));
  const currentStatus = statusIndex >= 0 ? statuses[statusIndex] : undefined;

  if (mode === "current") {
    return currentStatus?.name ?? null;
  }

  if (mode === "all") {
    // Match PDF export: only dated milestones with a real status name (no "Status" fallback).
    const dated = (statusLine ?? [])
      .filter((entry) => entry.dueDateKey && entry.name?.trim())
      .sort((a, b) => a.rank - b.rank);
    if (dated.length === 0) return null;
    return dated
      .map((entry) => `${entry.name!.trim()} by ${formatDate(entry.dueDateKey as string)}`)
      .join("\n");
  }

  // The item is final when it sits at a completion status or the terminal (last) pipeline stage.
  const isLast = statusIndex >= 0 && statusIndex === statuses.length - 1;
  const isFinal = currentStatus?.isCompletionStatus === true || isLast;
  const finalStatusName = statuses.length > 0 ? statuses[statuses.length - 1].name : null;

  // The item's pipeline rank: prefer its status-line entry, else its display-order index.
  let itemRank: number | null = null;
  const ownEntry = statusLine?.find((entry) => String(entry.statusId) === String(statusId));
  if (ownEntry) {
    itemRank = ownEntry.rank;
  } else if (statusIndex >= 0) {
    itemRank = statusIndex;
  }

  // 1. Next dated milestone strictly ahead of the item's current rank.
  if (itemRank !== null) {
    const nextDated = (statusLine ?? [])
      .filter((entry) => entry.dueDateKey)
      .sort((a, b) => a.rank - b.rank)
      .find((entry) => entry.rank > itemRank!);
    if (nextDated) {
      return `→ ${nextDated.name ?? "Status"} by ${formatDate(nextDated.dueDateKey as string)}`;
    }
  }

  // 2. The final status due by the task's top-level finish date, when the item is not yet final.
  if (!isFinal && taskFinishDateKey && finalStatusName) {
    return `→ ${finalStatusName} by ${formatDate(taskFinishDateKey)}`;
  }

  // 3. Completed: show the current status with the date associated with it (no "next" arrow).
  if (isFinal) {
    const completedName = currentStatus?.name?.trim() || "Finished";
    const completedDateKey = ownEntry?.dueDateKey || (isLast ? taskFinishDateKey : null);
    if (completedDateKey) {
      return `${completedName} by ${formatDate(completedDateKey)}`;
    }
    return completedName;
  }

  return null;
}
